import { createContext, useContext, useEffect, useState } from "react";
import { onIdTokenChanged, signOut } from "firebase/auth";
import { auth, FIREBASE_READY } from "../lib/firebase";
import api from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!FIREBASE_READY) { setLoading(false); return; }
    const unsub = onIdTokenChanged(auth, async (fbUser) => {
      if (fbUser) {
        try {
          const token = await fbUser.getIdToken();
          localStorage.setItem("token", token);
          const { data } = await api.get("/auth/me");
          setUser(data);
          try { localStorage.setItem("tp_auth_user", JSON.stringify(data)); } catch { /* storage full */ }
        } catch (err) {
          if (!err?.response) {
            // API unreachable (offline) but Firebase session is valid — keep the
            // last-known profile so cached trips stay reachable in Offline Travel Mode.
            let cached = null;
            try { cached = JSON.parse(localStorage.getItem("tp_auth_user") || "null"); } catch { /* corrupt */ }
            const sameAccount = cached && (!cached.email || !fbUser.email ||
              cached.email.toLowerCase() === fbUser.email.toLowerCase());
            setUser(sameAccount ? cached : null);
          } else {
            setUser(null); // server explicitly rejected the session
          }
        }
      } else {
        localStorage.removeItem("token");
        localStorage.removeItem("tp_auth_user");
        setUser(null);
      }
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const logout = async () => {
    try { if (auth) await signOut(auth); } catch {}
    localStorage.removeItem("token");
    localStorage.removeItem("tp_auth_user");
    setUser(null);
  };

  // 12h idle limit ("Keep me signed in on this device"): activity refreshes a
  // timestamp; a periodic check signs the user out after 12h of inactivity.
  useEffect(() => {
    if (!user) return;
    const IDLE_MS = 12 * 60 * 60 * 1000;
    let lastWrite = 0;
    const touch = () => {
      const now = Date.now();
      if (now - lastWrite > 60000) { lastWrite = now; localStorage.setItem("tp_last_activity", String(now)); }
    };
    touch();
    const events = ["pointerdown", "keydown", "scroll"];
    events.forEach((e) => window.addEventListener(e, touch, { passive: true }));
    const check = () => {
      const last = Number(localStorage.getItem("tp_last_activity") || Date.now());
      if (Date.now() - last > IDLE_MS) logout();
    };
    check();
    const iv = setInterval(check, 5 * 60 * 1000);
    return () => { events.forEach((e) => window.removeEventListener(e, touch)); clearInterval(iv); };
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AuthContext.Provider value={{ user, setUser, loading, logout, firebaseReady: FIREBASE_READY }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
